<template>
    <div class="add-holdings">
        <workflow-layout>
            <template v-slot:header>
                <workflow-header
                    mwc-id="workflow-header"
                    :key="$route.params.portfolioNumber + 'header'"
                    :component-config="headerConfig"
                    v-on="events"
                ></workflow-header>
            </template>
            <portfolio-actions :mwc-id="getMwcIdForChild('portfolioActions')" :portfolioIndex="$route.params.portfolioNumber"></portfolio-actions>
            <div class="portfolio-construction" :class="[{ 'investment-workflow': this.isInvestmentWorkflow }]">
                <portfolio-construction-tool
                    id="HYPO_PCT"
                    mwc-id="HYPO_PCT"
                    v-if="showPCT"
                    :is-investment-comparison="isInvestmentWorkflow"
                    :comparison-options="PCTConfig.comparisonOptions"
                    :ec-api-token="PCTConfig.ecApiToken"
                    :culture-id="PCTConfig.cultureId"
                    :caller="PCTConfig.caller"
                    :initial-tool-id="PCTConfig.toolId"
                    :price-type="PCTConfig.priceType"
                    :initial-allocate-by="PCTConfig.allocateBy"
                    :default-amount="PCTConfig.defaultAmount"
                    :initial-currency="PCTConfig.currency"
                    :options-list="PCTConfig.optionList"
                    :universes-config-list="PCTConfig.universes"
                    :notification-bubble="PCTConfig.notificationBubble"
                    :initial-asset-allocation-config="PCTConfig.assetAllocation"
                    :initial-holding-limit="PCTConfig.holdingLimit"
                    :show-risk-score="PCTConfig.showRiskScore"
                    :show-asset-allocation="PCTConfig.showAssetAllocation"
                    :include-risk-score-column="PCTConfig.includeRiskScoreColumn"
                    :initial-list-items="PCTsettings.PCTData.listItems"
                    :initial-total-amount="PCTsettings.PCTData.totalAmount"
                    :risk-score-props="riskScoreProps"
                    style="overflow-x:auto;"
                    @list-changed="handleListChange"
                    @error-check="handlePCTError"
                    @ec-component-token-expired="handleTokenExpired"
                    :key="$route.params.portfolioNumber + 'pct'"
                />
                <mds-loader v-else></mds-loader>
            </div>
        </workflow-layout>
        <header-dialog
            :mwc-id="getMwcIdForChild('headerDialog')"
            :show="showDialog"
            :headerDialogConfig="dialogConfig"
            @dialog-close="dialogCloseHandler"
        ></header-dialog>
        <save-dialog :mwc-id="getMwcIdForChild('saveDialog')" :show="showSaveDialog" @save-event="handleSaveDialog"></save-dialog>
    </div>
</template>

<script>
import MdsLoader from "@mds/loader";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import HypoWorkflowMixin from "@/mixins/HypoWorkflowMixin";
import HeaderConfigMixin from "@/mixins/HeaderConfigMixin";
import * as types from "@/store/mutation-types.js";
import WorkflowLayout from "../layouts/WorkflowLayout";
import WorkflowHeader from "hypo-workflow-header/src/WorkflowHeader";
import PortfolioActions from "./PortfolioActions";
import HeaderDialog from "./HeaderDialog";
import SaveDialog from "./SaveDialog";
import { getPCTEnv } from "@/util";
import { setTimePeriodForSecurities, getGlobalTimePeriodAvailableDates, updatePortfolioByShares } from "@/common/timePeriod.js";
import { importFromFederatedModule, useCdnAsset } from "@/microfrontend";

export default {
    name: "add-holdings",
    mixins: [MwcSharedMixin, HypoWorkflowMixin, HeaderConfigMixin],
    components: {
        MdsLoader,
        WorkflowLayout,
        WorkflowHeader,
        PortfolioConstructionTool: () => importFromFederatedModule("awse_portfolio_construction_tool/main").then(module => module.default),
        HeaderDialog,
        SaveDialog,
        PortfolioActions
    },
    data() {
        return {
            events: {
                continue: () => this.continueHandler(this),
                back: () => this.backHandler(this),
                close: () => this.closeHandler(this),
                closeNotSave: () => this.closeNotSaveHandler(this),
                rename: () => this.renameHandler(this),
                delete: () => this.deleteHandler(this)
            },
            portfolioNumber: this.$route.params.portfolioNumber,
            hasError: false,
            skipNextListChanged: false,
            portfolio: {},
            PCTsettings: {
                PCTConfig: {
                    caller: "HYPO",
                    toolId: "HYPO",
                    env: "STG",
                    priceType: "1",
                    allocateBy: "1",
                    defaultAmount: 10000,
                    currency: "USD",
                    token: "",
                    unsupportedUniverses: [],
                    universes: [],
                    listFields: [43988, 44097, 43978, 52648], // List column fields
                    portfolioFields: [44888, 44824, 44997, 44878, 53442, 45103, 45020], //Portfolio or Model Portfolio column fields
                    cultureId: "en-US",
                    optionList: [],
                    assetAllocation: {},
                    notificationBubble: false,
                    holdingLimit: {
                        min: 1,
                        max: 80
                    },
                    showRiskScore: true,
                    showAssetAllocation: true,
                    includeRiskScoreColumn: true,
                    ecApiToken: ""
                },
                PCTData: {
                    listItems: [],
                    totalAmount: ""
                }
            },
            showDialog: false,
            showSaveDialog: false,
            dialogConfig: {},
            showPCT: false,
            savingPortfolio: 0,
            riskScoreProps: {
                region: "USA"
            }
        };
    },
    mounted() {
        const Util = useCdnAsset("awse_core", "Util");
        this.dialogConfig = this.defaultDialogConfig;
        this.setEditingPortfolio(parseInt(this.portfolioNumber));
        const { priceType = "1", currency } = this.portfolio;
        /* istanbul ignore next */
        this.PCTsettings.PCTConfig.env =
            process.env.VUE_APP_USAGE === "application" ? getPCTEnv(this.settings.environment) : this.settings.environment.toUpperCase().split("-")[0];
        if (this.settings.caller) {
            this.PCTsettings.PCTConfig.caller = this.settings.caller;
        }
        this.PCTsettings.PCTConfig.priceType = priceType;
        this.PCTsettings.PCTConfig.allocateBy = this.useAllocateBy();
        this.PCTsettings.PCTConfig.currency = currency;
        this.PCTsettings.PCTConfig.defaultAmount = this.$store.state.preference.initialInvestmentAmount;
        this.PCTsettings.PCTConfig.optionList = this.$store.getters["config/getPCTOptionList"];
        this.PCTsettings.PCTConfig.universes = this.$store.getters["config/getUniverses"];
        this.PCTsettings.PCTConfig.assetAllocation = this.$store.getters["hypo/getPCTAssetAllocation"];
        this.PCTsettings.PCTConfig.holdingLimit.max = this.isInvestmentWorkflow ? 5 : this.$store.getters["config/securityLimit"];
        this.PCTsettings.PCTConfig.showRiskScore = this.settings.showRiskScore && !this.isInvestmentWorkflow;
        this.PCTsettings.PCTConfig.includeRiskScoreColumn = this.settings.includeRiskScoreColumn;
        this.PCTsettings.PCTConfig.showAssetAllocation = !this.isInvestmentWorkflow;
        this.PCTsettings.PCTData.listItems = this.mapPortfolioData();
        this.PCTsettings.PCTConfig.comparisonOptions = this.getComparisonOptions();
        this.PCTsettings.PCTData.totalAmount = this.getPortfolioTotalAmount(this.portfolio);
        Util.setGlobalSettings("PCTConfig", this.PCTsettings.PCTConfig);
        this.getToken().then(token => {
            window.sessionStorage.setItem("uim-auth", token);
            this.PCTsettings.PCTConfig.token = token;
            this.PCTsettings.PCTConfig.ecApiToken = token;
            this.overwritePCTConfigData();
            this.showPCT = true;
        });
    },
    watch: {
        "$route.params.portfolioNumber": {
            async handler(newVal) {
                this.showPCT = false;
                while (this.savingPortfolio > 0) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                this.portfolioNumber = newVal;
                this.setEditingPortfolio(newVal);
                this.updatePCTConfig();
                this.showPCT = true;
            }
        }
    },
    methods: {
        handleTokenExpired(cb) {
            this.getToken().then(token => {
                cb(token);
            });
        },
        async getToken() {
            return Promise.resolve().then(() => {
                const token = window.localStorage.getItem("UIMToken");
                if (token) {
                    return token;
                } else {
                    return this.getPCTToken().then(data => data.token);
                }
            });
        },
        overwritePCTConfigData() {
            window.mwc.configuration.setAppConfig({
                components: {
                    HYPO_PCT: {
                        settings: this.PCTsettings
                    }
                }
            });
        },
        getComparisonOptions() {
            const isPortfolioComparison = parseInt(this.$store.getters["hypo/getType"]) === 1;
            if (isPortfolioComparison) {
                const comparisonPortfolioIndex = parseInt(this.portfolioNumber) === 0 ? 1 : 0;
                const comparisonPortfolio = this.$store.state.investments.portfolioList[comparisonPortfolioIndex];
                const syncTotalAmount = this.getPortfolioTotalAmount(comparisonPortfolio);
                return {
                    syncTotalAmount: syncTotalAmount,
                    syncActionText: `${this.getUILabel("sync_portfolio_total_amount")} ${comparisonPortfolioIndex + 1}`
                };
            }
            return null;
        },
        useAllocateBy() {
            const { allocateBy, pctAllocateBy } = this.portfolio;
            return typeof pctAllocateBy !== "undefined" ? `${pctAllocateBy}` : `${allocateBy}`;
        },
        updatePCTConfig() {
            this.PCTsettings.PCTConfig.priceType = this.portfolio.priceType;
            this.PCTsettings.PCTConfig.allocateBy = this.useAllocateBy();
            this.PCTsettings.PCTData.listItems = this.mapPortfolioData();
            this.PCTsettings.PCTData.totalAmount = this.getPortfolioTotalAmount(this.portfolio);
            this.PCTsettings.PCTConfig.comparisonOptions = this.getComparisonOptions();
            this.overwritePCTConfigData();
        },
        handlePCTError(check) {
            this.hasError = check;
        },
        async handleListChange(obj) {
            // handleListChange() can be trigger multiple times simultaneously
            // So only savingPortfilio === 0 means no running handleListChange functions
            this.savingPortfolio++;
            const { securities, allocateby, asofdate } = obj.final;
            const secIds = securities.map(item => item.secId);
            const promises = [];
            try {
                await this.getSecuritiesInfo(secIds).then(data => {
                    promises.push(this.updateSecuritiesInfo(data, securities, allocateby, asofdate));
                });
                await Promise.all(promises);
            } finally {
                this.savingPortfolio--;
            }
        },
        async updateSecuritiesInfo(securitiesInfo, securities, allocateBy, priceType) {
            const { portfolioList } = this.$store.state.investments;
            const updatedPortfolio = Object.assign({}, portfolioList[this.portfolioNumber]);
            const { securities: oldSecurities } = updatedPortfolio;
            // set securities base information like dates and investment amount
            const newSecurities = securitiesInfo.map(item => {
                const security = securities.find(i => i.secId === item.securityId);
                const oldSecurity = oldSecurities.find(i => i.securityId === item.securityId);
                return {
                    ...oldSecurity,
                    securityId: item.securityId,
                    securityName: item.securityName,
                    securityType: item.securityType,
                    ticker: item.ticker,
                    baseCurrency: item.currencyId,
                    identifier: security.identifier,
                    firstPriceDate: item.firstPriceDate,
                    inceptionDate: item.inceptionDate,
                    lastPriceDate: item.lastPriceDate,
                    investAmount: security.amount,
                    investPercent: security.weight,
                    investShares: security.share,
                    price: security.price // store pct security price at as of date
                };
            });
            updatedPortfolio.securities = newSecurities;
            const allocateByMethod = parseInt(allocateBy);
            updatedPortfolio.pctAllocateBy = allocateByMethod;
            updatedPortfolio.allocateBy = allocateByMethod === 2 ? 0 : allocateByMethod;
            updatedPortfolio.priceType = priceType;
            updatedPortfolio.totalInvestAmount = newSecurities.reduce((prev, item) => prev + item.investAmount, 0);
            return this.$store
                .dispatch("investments/addSecuritiesfromPCT", {
                    updatedPortfolio,
                    index: parseInt(this.portfolioNumber)
                })
                .then(async () => {
                    await this.setTimePeriodForPortfolioList();
                });
        },
        async setTimePeriodForPortfolioList() {
            const { portfolioList } = this.$store.state.investments;
            const hasSecurities = portfolioList.some(portfolio => portfolio.securities.length > 0);
            if (!hasSecurities) {
                return;
            }
            const isApplyEarliestAvailable = this.$store.getters["config/isApplyEarliestAvailable"];
            let { timePeriod } = this.$store.state.hypo;
            let presetTimePeriod, defaultDateRange;
            const globalAvailableDates = getGlobalTimePeriodAvailableDates({ portfolioList });
            // first portfolio with time period assumptions.
            const defaultTimePeriodAssumptionPortfolio = portfolioList.find(portfolio => {
                const { securities } = portfolio;
                return securities.length > 0 && securities.some(security => "timePeriod" in security);
            });
            const updatedPortfolioList = portfolioList.map(portfolio => {
                let securities = portfolio.securities;
                if (defaultTimePeriodAssumptionPortfolio) {
                    const firstSecurity = defaultTimePeriodAssumptionPortfolio.securities[0];
                    timePeriod = firstSecurity.timePeriod;
                    presetTimePeriod = firstSecurity.presetTimePeriod;
                    defaultDateRange = {
                        startDate: firstSecurity.startDate,
                        endDate: firstSecurity.endDate
                    };
                }
                securities = setTimePeriodForSecurities({
                    securities: securities,
                    timePeriod,
                    presetTimePeriod,
                    defaultDateRange,
                    globalAvailableDates,
                    isApplyEarliestAvailable
                });
                return {
                    ...portfolio,
                    securities
                };
            });
            //HYPO-4832, carry pct amount instead of share. keep it temporarily and can be remove if assumption is always based on allocate by amount/percent.
            await Promise.all(
                updatedPortfolioList.map(async portfolio => {
                    return updatePortfolioByShares({
                        portfolio,
                        querySecurities: portfolio.securities,
                        securityPriceMap: {},
                        fetch: this.$fetch
                    });
                })
            ).then(updatedPortfolioList => {
                this.$store.commit(`investments/${types.UPDATE_PORTFOLIOLIST}`, updatedPortfolioList);
                this.$store.commit(`investments/${types.UPDATE_RAW_PORTFOLIOLIST}`, updatedPortfolioList);
            });
        },
        setEditingPortfolio(index) {
            const { portfolioList } = this.$store.state.investments;
            this.portfolio = portfolioList[index];
            this.$store.commit(`investments/${types.SET_EDITING_PORTFOLIO_ID}`, portfolioList[index].portfolioId);
        },
        getPortfolioTotalAmount(portfolio) {
            //Recalculate PCT total amount at as of date.
            const { securities } = portfolio;
            const allocateBy = this.useAllocateBy();
            return securities.reduce((prev, security) => {
                // use pct invest amount at as of date.
                const amount = allocateBy === 2 ? security.investShares * security.price : security.investAmount;
                return prev + amount;
            }, 0);
        },
        mapPortfolioData() {
            const { securities } = this.portfolio;
            const allocateBy = this.useAllocateBy();
            const totalAmount = this.getPortfolioTotalAmount(this.portfolio);
            return securities.map(item => {
                const investAmount = allocateBy === 2 ? item.investShares * item.price : item.investAmount;
                return {
                    secId: item.securityId,
                    secName: item.securityName,
                    weight: (investAmount / totalAmount) * 100,
                    amount: investAmount,
                    share: item.investShares,
                    price: item.price
                };
            });
        },
        continueHandler(instance) {
            this.saveLastStep(this.currentStep.keyString);
            this.$router.push(this.currentStep.nextRoute.route);
        },
        closeHandler(instance) {
            this.saveLastStep(this.currentStep.keyString);
            this.checkHypoCount();
        },
        closeNotSaveHandler(instance) {
            this.$store.commit(`hypo/${types.SET_FIRM_SAVED}`, false);
            this.$store.dispatch("clearHypoInput");
            this.$router.push("/type");
        },
        backHandler(instance) {
            if (!this.currentStep.previousRoute) {
                this.$router.push("/type");
            } else {
                this.$router.push(this.currentStep.previousRoute);
            }
        },
        renameHandler(instance) {
            this.dialogConfig = this.getDialogConfig("rename", "hypo", null);
            this.showDialog = true;
        },
        deleteHandler(instance) {
            this.dialogConfig = this.getDialogConfig("delete", "hypo", null);
            this.showDialog = true;
        }
    },
    computed: {
        isInvestmentWorkflow() {
            return parseInt(this.$store.getters["hypo/getType"], 10) === 2;
        },
        PCTConfig() {
            return this.PCTsettings.PCTConfig;
        },
        headerConfig() {
            const editingPortfolio = this.$store.getters["investments/editingPortfolio"];
            const isNoHoldings = editingPortfolio && editingPortfolio.securities.length === 0;
            const primaryButtonState = editingPortfolio && editingPortfolio.securities.length !== 0 && !this.hasError;
            return {
                leftButton: {
                    type: "secondary",
                    isActive: isNoHoldings || !this.hasError,
                    isVisible: true,
                    label: this.$store.state.hypo.firmSaved ? this.getUILabel("COMMON_CLOSE") : this.getUILabel("COMMON_SAVE_AND_CLOSE"),
                    actionTrigger: this.$store.state.hypo.firmSaved ? "closeNotSave" : "close"
                },
                middleButton: {
                    type: "secondary",
                    isActive: true,
                    isVisible: true,
                    label: this.portfolioNumber === "0" ? this.getUILabel("COMMON_CANCEL") : this.getUILabel("COMMON_PREVIOUS"),
                    actionTrigger: "back"
                },
                rightButton: {
                    type: "primary",
                    isActive: primaryButtonState,
                    isVisible: true,
                    label: this.getUILabel("COMMON_NEXT"),
                    actionTrigger: "continue"
                },
                ...this.getTitleAndStepperConfig()
            };
        }
    }
};
</script>

<style lang="scss">
// Temporarily add this to avoid the tooltip on body cause the scrollbar unexpected appeared.
.mds-tooltip___hypo.mds-tooltip--top-center___hypo {
    top: -100%;
}
</style>

<style lang="scss" scoped>
@import "~@mds/typography";
.portfolio-construction {
    width: 100%;
    height: calc(100% - 48px); //48 = actions
    &.investment-workflow {
        height: 100%;
    }
    ::v-deep .awse-portfolio-construction-tool-layout .portfolio-construction-tool-layout-body {
        padding: 0px;
    }
}
.section-title {
    @include mds-level-6-heading($bold: false);
    padding-bottom: $mds-space-1-x;
    color: $mds-text-color-primary;
    border-bottom: $mds-border-separator;
}
.add-holdings {
    height: inherit;
}
.add-holdings .content {
    min-height: 100%;
}

.add-holdings ::v-deep .content__inner {
    height: 100%;
}
</style>
